/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { FirebaseContext, useAuth } from "../Firebase"

import Header from "../header/PublicHeader"
import WingsAppNav from "../navigation/WingsAppNav/WingsAppNav"
import Footer from "../footer/footer"
import LevelsModal from "../levels/LevelsModal.js"
// Bootstrap styles import needs to load before our global styles import so that
// our styles win any specificity ties
import "bootstrap/dist/css/bootstrap.min.css"
import "../../global-styles/index.css"
import "./layout.css"
import SimpleLocalize from "../SimpleLocalize/SimpleLocalize"
import { walkThroughSteps } from "../get-started/utils/utils"

import { Router } from "@reach/router"
import Dashboard from "../dashboard/dashboard"
import englishMessages from "../../../lang/en-US.json"
import spanishMessages from "../../../lang/es-ES.json"
import PrivateRoute from "../Firebase/PrivateRoute"
import MyAccount from "../private/account"
import Subscription from "../private/subscription"
import CancelMembership from "../private/cancel"
import { getCurrentPageLanguage } from "../../linkUtils"
import ProfilePage from "../../pages/profile"
import { ProgramHome } from "../curriculum/ProgramHome.js"
import { ModuleHome } from "../curriculum/ModuleHome.js"
import InteractiveModule from "../curriculum/InteractiveModule.js"
import CertificatePage from "../certificates/CertificatePage.js"

import { Toaster } from "sonner"

import MaintenancePage from "../maintenance/maintenance.js"
import useSubdomain from "../hooks/useSubdomain.js"
import { navigate } from "gatsby"

// if localization is enabled, show language switcher and allow user to change language
const localizationEnabled = process.env.GATSBY_ENABLE_LOCALIZATION === "true"

// if maintenance is enabled, allow bypass for development on branch
const isMaintenanceBypassOn = process.env.GATSBY_MAINTENANCE_BYPASS === "true"

// Main layout component for entire site
const Layout = (props) => {
  const { user, firebase, loading, profile, updateAssessment, globalSiteConfig, localFirestore, notifier, schoolMemberships, isUserLoggedOut } = useAuth()
  const subdomainConfig = useSubdomain()
  const { isWhitelistedSubdomain } = subdomainConfig
  const { children } = props

  const [signing, setSigning] = useState(false)
  const [pointTotals, setPointTotals] = useState(null)
  const [level, setLevel] = useState(null)
  const [walkThroughStepValues, setWalkThroughStepValues] = useState(walkThroughSteps)
  const [prospectivePreviewCode, setProspectivePreviewCode] = useState("")

  const data = useStaticQuery(graphql`
    query SiteTitleQuery {
      site {
        siteMetadata {
          title
        }
      }
    }
  `)

  const locale = getCurrentPageLanguage(props.location?.pathname || "")

  let pageContext = locale === "es-ES" ? { language: "es-ES", messages: spanishMessages } : { language: "en-US", messages: englishMessages }

  useEffect(() => {
    if (!isWhitelistedSubdomain) {
      navigate("https://www.wingspans.com/", { replace: true })
    }
  }, [isWhitelistedSubdomain])

  return (
    <>
    <Helmet defaultTitle="Wingspans" />
    <FirebaseContext.Provider
      value={{
        user,
        firebase,
        loading,
        isUserLoggedOut,
        profile,
        signing,
        setSigning,
        pointTotals,
        setPointTotals,
        walkThroughStepValues,
        setWalkThroughStepValues,
        updateAssessment,
        subdomainConfig,
        level,
        setLevel,
        localFirestore,
        notifier,
        schoolMemberships,
        prospectivePreviewCode,
        setProspectivePreviewCode,
      }}
    >
      {globalSiteConfig?.isMaintenanceOn && !isMaintenanceBypassOn ? (
        <MaintenancePage />
      ) : (
        <SimpleLocalize {...{ pageContext }}>
          <Header siteTitle={data.site.siteMetadata.title} />
          <WingsAppNav />
          {!notifier.isDisabled && (
            <Toaster
              toastOptions={{
                style: {
                  boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
                },
                className: "toaster",
              }}
              pauseWhenPageIsHidden={true}
            />
          )}

          {/* certificate pages - public, no navigation - MUST BE FIRST */}
          <Router basepath="/">
            <CertificatePage path="/certificates/:certUUID" />
          </Router>

          <LevelsModal />

          {/* public dashboards in both languages */}
          <Router basepath="/">
            <Dashboard path={"/dashboard"} pageContext={{ language: "en-US", messages: englishMessages }} />
            <Dashboard path={"/es-ES/dashboard"} pageContext={{ language: "es-ES", messages: spanishMessages }} />
          </Router>

          {/* member pages in both languages, private */}
          <Router basepath="/member">
            <PrivateRoute path="/account" component={MyAccount} pageContext={{ language: "en-US", messages: englishMessages }} />
            <PrivateRoute path="/subscription" component={Subscription} pageContext={{ language: "en-US", messages: englishMessages }} />
            <PrivateRoute path="/cancel" component={CancelMembership} pageContext={{ language: "en-US", messages: englishMessages }} />

            <PrivateRoute path="/es-ES/account" component={MyAccount} pageContext={{ language: "es-ES", messages: spanishMessages }} />
            <PrivateRoute path="/es-ES/subscription" component={Subscription} pageContext={{ language: "es-ES", messages: spanishMessages }} />
            <PrivateRoute path="/es-ES/cancel" component={CancelMembership} pageContext={{ language: "es-ES", messages: spanishMessages }} />
          </Router>

          {/* spanish public profile */}
          <Router basepath="/es-ES">
            <ProfilePage path="/profile/*" />
          </Router>

          <Router basepath="/">
            <ProgramHome path="/programs/:programId" pageContext={{ language: "en-US", messages: englishMessages }}/>
            <ProgramHome path="/es-ES/programs/:programId" pageContext={{ language: "es-ES", messages: spanishMessages }}/>
          </Router>

          <Router basepath="/">
            <ModuleHome path="/modules/:moduleId" pageContext={{ language: "en-US", messages: englishMessages }}/>
            <ModuleHome path="/es-ES/modules/:moduleId" pageContext={{ language: "es-ES", messages: spanishMessages }} />
          </Router>

          <Router basepath="/">
            <InteractiveModule path="/modules/:moduleId/interactive" pageContext={{ language: "en-US", messages: englishMessages }}/>
            <InteractiveModule path="/es-ES/modules/:moduleId/interactive" pageContext={{ language: "es-ES", messages: spanishMessages }}/>
          </Router>

          {/* handles most content, pages built during build process */}
          <main id="main-content" tabIndex={-1}>
            {children}
          </main>

          {/* footer at bottom of all pages */}
          <Footer />
        </SimpleLocalize>
      )}
    </FirebaseContext.Provider>
    </>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
